/**
 * FiscalSefazPage - Página Completa de Gestão Fiscal SEFAZ
 * 
 * Funcionalidades:
 * - Gestão de Certificados A1 (upload, validação, ativação)
 * - Manifestação do Destinatário
 * - Lista de Documentos NF-e com download de XML
 * - Sincronização com SEFAZ
 */

import React, { useState, useEffect, useCallback } from 'react';
import { MainLayout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  FileText, Upload, Shield, RefreshCw, Download, Eye, Package, 
  CheckCircle2, XCircle, Clock, AlertTriangle, Search,
  Building2, Calendar, Key, Trash2, MoreVertical, FileCode,
  Send, ThumbsUp, ThumbsDown, HelpCircle, Loader2, Filter,
  CheckSquare, AlertOctagon
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// CNPJ fixo da empresa
const COMPANY_CNPJ = '63420089000165';

// Tipos de manifestação
const MANIFEST_TYPES = [
  { value: 'CIENCIA', label: 'Ciência da Operação', icon: HelpCircle, color: 'blue', description: 'Confirma que você tomou ciência da NF-e' },
  { value: 'CONFIRMACAO', label: 'Confirmação da Operação', icon: ThumbsUp, color: 'green', description: 'Confirma que a operação foi realizada' },
  { value: 'DESCONHECIMENTO', label: 'Desconhecimento da Operação', icon: ThumbsDown, color: 'orange', description: 'Indica que você desconhece a operação' },
  { value: 'NAO_REALIZADA', label: 'Operação Não Realizada', icon: XCircle, color: 'red', description: 'Indica que a operação não foi realizada' },
];

// Formatar CNPJ
const formatCNPJ = (cnpj) => {
  if (!cnpj) return '-';
  const clean = cnpj.replace(/\D/g, '');
  if (clean.length !== 14) return cnpj;
  return clean.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
};

// Formatar valor
const formatCurrency = (value) => {
  if (!value && value !== 0) return '-';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
};

// Formatar data
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return dateStr;
  }
};

// Status badge para documentos
const getStatusBadge = (status) => {
  const statusMap = {
    'pending': { label: 'Pendente', variant: 'secondary', icon: Clock },
    'imported': { label: 'Importado', variant: 'default', icon: CheckCircle2 },
    'processed': { label: 'Processado', variant: 'default', icon: CheckCircle2 },
    'error': { label: 'Erro', variant: 'destructive', icon: XCircle },
    'complete': { label: 'Completo', variant: 'default', icon: CheckCircle2 },
  };
  const config = statusMap[status] || { label: status, variant: 'secondary', icon: Clock };
  const Icon = config.icon;

  return (
    <Badge variant={config.variant} className="flex items-center gap-1">
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
};

// Status badge para manifestação
const getManifestBadge = (manifestStatus) => {
  if (!manifestStatus) {
    return <Badge variant="outline" className="text-slate-500">Não manifestado</Badge>;
  }
  const statusMap = {
    'CIENCIA': { label: 'Ciência', color: 'bg-blue-100 text-blue-800 border-blue-200' },
    'CONFIRMACAO': { label: 'Confirmado', color: 'bg-green-100 text-green-800 border-green-200' },
    'DESCONHECIMENTO': { label: 'Desconhecido', color: 'bg-orange-100 text-orange-800 border-orange-200' },
    'NAO_REALIZADA': { label: 'Não Realizada', color: 'bg-red-100 text-red-800 border-red-200' },
  };
  const config = statusMap[manifestStatus] || { label: manifestStatus, color: 'bg-slate-100 text-slate-800' };
  return <Badge className={config.color + ' border'}>{config.label}</Badge>;
};

export default function FiscalSefazPage() {
  // Estados principais
  const [activeTab, setActiveTab] = useState('documents');
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  
  // Estados de certificados
  const [certificates, setCertificates] = useState([]);
  const [loadingCerts, setLoadingCerts] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPassword, setUploadPassword] = useState('');
  const [uploading, setUploading] = useState(false);
  
  // Estados de documentos
  const [documents, setDocuments] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [documentDetailOpen, setDocumentDetailOpen] = useState(false);
  const [documentXml, setDocumentXml] = useState(null);
  
  // Estados de manifestação
  const [manifestDialogOpen, setManifestDialogOpen] = useState(false);
  const [manifestType, setManifestType] = useState('');
  const [manifestJustification, setManifestJustification] = useState('');
  const [manifesting, setManifesting] = useState(false);
  
  // Estados de filtros
  const [searchKey, setSearchKey] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [manifestFilter, setManifestFilter] = useState('all');
  
  // Estatísticas
  const [stats, setStats] = useState({
    total_documents: 0,
    pending_import: 0,
    imported: 0,
    active_certificates: 0,
  });

  // Estados de importação para estoque
  const [stockImportDialogOpen, setStockImportDialogOpen] = useState(false);
  const [nfeItems, setNfeItems] = useState([]);
  const [loadingNfeItems, setLoadingNfeItems] = useState(false);
  const [stockItems, setStockItems] = useState([]);
  const [itemMappings, setItemMappings] = useState({});
  const [importingToStock, setImportingToStock] = useState(false);

  // Carregar itens de estoque
  const loadStockItems = async () => {
    try {
      const response = await fetch(API_URL + '/api/stock/items', {
        headers: { 'Authorization': 'Bearer ' + localStorage.getItem('token') }
      });
      if (response.ok) {
        const data = await response.json();
        setStockItems(data.items || data || []);
      }
    } catch (error) {
      console.error('Erro ao carregar itens de estoque:', error);
    }
  };

  // Abrir modal de importação para estoque
  const openStockImportDialog = async (doc) => {
    setSelectedDocument(doc);
    setItemMappings({});
    setNfeItems([]);
    setLoadingNfeItems(true);
    setStockImportDialogOpen(true);
    
    await loadStockItems();
    
    try {
      const response = await fetch(API_URL + '/api/sefaz/documents/' + doc.id + '/items-preview', {
        headers: { 'Authorization': 'Bearer ' + localStorage.getItem('token') }
      });
      if (response.ok) {
        const data = await response.json();
        setNfeItems(data.items || []);
        if (data.available_stock_items) {
          setStockItems(data.available_stock_items);
        }
      } else {
        setNfeItems([]);
        toast.error('Erro ao carregar itens da NF-e');
      }
    } catch (error) {
      console.error('Erro ao carregar itens:', error);
      setNfeItems([]);
    } finally {
      setLoadingNfeItems(false);
    }
  };

  // Forçar atualização do status de manifestação
  const handleForceManifestStatus = async (doc, manifestType = 'CIENCIA') => {
    const confirmMsg = 'Esta NF-e já foi manifestada no portal SEFAZ? Ao confirmar, o status será atualizado para ' + manifestType + '.';
    if (!window.confirm(confirmMsg)) {
      return;
    }
    
    try {
      const response = await fetch(API_URL + '/api/sefaz/documents/' + doc.id + '/force-manifest-status', {
        method: 'POST',
        headers: {
          'Authorization': 'Bearer ' + localStorage.getItem('token'),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ manifest_type: manifestType })
      });
      
      if (response.ok) {
        toast.success('Status de manifestação atualizado com sucesso!');
        loadDocuments();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao atualizar status');
      }
    } catch (error) {
      console.error('Erro:', error);
      toast.error('Erro ao atualizar status de manifestação');
    }
  };

  // Carregar certificados
  const loadCertificates = useCallback(async () => {
    setLoadingCerts(true);
    try {
      const response = await fetch(API_URL + '/api/sefaz/settings/certificates', {
        headers: { 'Authorization': 'Bearer ' + localStorage.getItem('token') }
      });
      if (response.ok) {
        const data = await response.json();
        setCertificates(data);
      } else {
        toast.error('Erro ao carregar certificados');
      }
    } catch (error) {
      console.error('Erro:', error);
      toast.error('Erro ao carregar certificados');
    } finally {
      setLoadingCerts(false);
    }
  }, []);

  // Carregar documentos
  const loadDocuments = useCallback(async () => {
    setLoadingDocs(true);
    try {
      let url = API_URL + '/api/sefaz/documents?limit=100';
      if (statusFilter !== 'all') url += '&status=' + statusFilter;
      
      const response = await fetch(url, {
        headers: { 'Authorization': 'Bearer ' + localStorage.getItem('token') }
      });
      if (response.ok) {
        const data = await response.json();
        setDocuments(data);
      } else {
        toast.error('Erro ao carregar documentos');
      }
    } catch (error) {
      console.error('Erro:', error);
      toast.error('Erro ao carregar documentos');
    } finally {
      setLoadingDocs(false);
    }
  }, [statusFilter]);

  // Carregar estatísticas
  const loadStats = useCallback(async () => {
    try {
      const response = await fetch(API_URL + '/api/sefaz/stats', {
        headers: { 'Authorization': 'Bearer ' + localStorage.getItem('token') }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  }, []);

  // Carregar dados iniciais
  useEffect(() => {
    loadCertificates();
    loadDocuments();
    loadStats();
  }, [loadCertificates, loadDocuments, loadStats]);

  // Recarregar documentos quando filtro muda
  useEffect(() => {
    loadDocuments();
  }, [statusFilter, loadDocuments]);

  // Upload de certificado
  const handleUploadCertificate = async () => {
    if (!uploadFile || !uploadPassword) {
      toast.error('Selecione o arquivo e informe a senha');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('certificate', uploadFile);
      formData.append('password', uploadPassword);
      formData.append('environment', 'PROD');

      const response = await fetch(API_URL + '/api/sefaz/settings/certificate', {
        method: 'POST',
        headers: { 'Authorization': 'Bearer ' + localStorage.getItem('token') },
        body: formData,
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success('Certificado enviado com sucesso!');
        setUploadDialogOpen(false);
        setUploadFile(null);
        setUploadPassword('');
        loadCertificates();
        loadStats();
      } else {
        toast.error(data.detail || 'Erro ao enviar certificado');
      }
    } catch (error) {
      console.error('Erro:', error);
      toast.error('Erro ao enviar certificado');
    } finally {
      setUploading(false);
    }
  };

  // Validar certificado
  const handleValidateCertificate = async (certId) => {
    try {
      const response = await fetch(API_URL + '/api/sefaz/settings/validate', {
        method: 'POST',
        headers: { 
          'Authorization': 'Bearer ' + localStorage.getItem('token'),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ certificate_id: certId })
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        toast.success('Certificado válido! ' + data.message);
      } else {
        toast.error(data.message || 'Erro na validação');
      }
    } catch (error) {
      console.error('Erro:', error);
      toast.error('Erro ao validar certificado');
    }
  };

  // Deletar certificado
  const handleDeleteCertificate = async (certId) => {
    if (!window.confirm('Tem certeza que deseja excluir este certificado?')) return;

    try {
      const response = await fetch(API_URL + '/api/sefaz/settings/certificate/' + certId, {
        method: 'DELETE',
        headers: { 'Authorization': 'Bearer ' + localStorage.getItem('token') }
      });

      if (response.ok) {
        toast.success('Certificado excluído');
        loadCertificates();
        loadStats();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Erro ao excluir');
      }
    } catch (error) {
      console.error('Erro:', error);
      toast.error('Erro ao excluir certificado');
    }
  };

  // Sincronizar com SEFAZ
  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await fetch(API_URL + '/api/sefaz/sync', {
        method: 'POST',
        headers: { 
          'Authorization': 'Bearer ' + localStorage.getItem('token'),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ environment: 'PROD', force: false })
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        toast.success('Sincronização concluída! ' + data.documents_new + ' novos documentos.');
        loadDocuments();
        loadStats();
      } else {
        toast.error(data.message || 'Erro na sincronização');
      }
    } catch (error) {
      console.error('Erro:', error);
      toast.error('Erro ao sincronizar');
    } finally {
      setSyncing(false);
    }
  };

  // Abrir detalhes do documento
  const handleViewDocument = async (doc) => {
    setSelectedDocument(doc);
    setDocumentDetailOpen(true);
    setDocumentXml(null);

    // Buscar XML completo se disponível
    try {
      const response = await fetch(API_URL + '/api/sefaz/documents/' + doc.id, {
        headers: { 'Authorization': 'Bearer ' + localStorage.getItem('token') }
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedDocument(data);
        if (data.xml_text) {
          setDocumentXml(data.xml_text);
        }
      }
    } catch (error) {
      console.error('Erro ao buscar detalhes:', error);
    }
  };

  // Buscar XML completo
  const handleFetchXml = async (docId) => {
    try {
      const response = await fetch(API_URL + '/api/sefaz/documents/' + docId + '/fetch-complete-xml', {
        method: 'POST',
        headers: { 
          'Authorization': 'Bearer ' + localStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        toast.success('XML obtido com sucesso!');
        handleViewDocument({ id: docId }); // Recarregar documento
        loadDocuments();
      } else {
        toast.error(data.message || 'Erro ao buscar XML. Manifeste o documento primeiro.');
      }
    } catch (error) {
      console.error('Erro:', error);
      toast.error('Erro ao buscar XML');
    }
  };

  // Abrir dialog de manifestação
  const handleOpenManifest = (doc) => {
    setSelectedDocument(doc);
    setManifestType('');
    setManifestJustification('');
    setManifestDialogOpen(true);
  };

  // Executar manifestação
  const handleManifest = async () => {
    if (!manifestType) {
      toast.error('Selecione o tipo de manifestação');
      return;
    }

    if ((manifestType === 'DESCONHECIMENTO' || manifestType === 'NAO_REALIZADA') && !manifestJustification) {
      toast.error('Justificativa obrigatória para este tipo de manifestação');
      return;
    }

    setManifesting(true);
    try {
      const response = await fetch(API_URL + '/api/sefaz/manifest', {
        method: 'POST',
        headers: { 
          'Authorization': 'Bearer ' + localStorage.getItem('token'),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          document_id: selectedDocument.id,
          manifest_type: manifestType,
          justification: manifestJustification || null
        })
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        toast.success('Manifestação realizada! Protocolo: ' + (data.protocol || 'N/A'));
        setManifestDialogOpen(false);
        loadDocuments();
      } else {
        toast.error(data.message || 'Erro na manifestação');
      }
    } catch (error) {
      console.error('Erro:', error);
      toast.error('Erro ao manifestar');
    } finally {
      setManifesting(false);
    }
  };

  // Importar por chave de acesso
  const handleImportByKey = async () => {
    if (searchKey.length !== 44) {
      toast.error('Chave de acesso deve ter 44 dígitos');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(API_URL + '/api/sefaz/import-by-key', {
        method: 'POST',
        headers: { 
          'Authorization': 'Bearer ' + localStorage.getItem('token'),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ access_key: searchKey, environment: 'PROD' })
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        toast.success('Documento importado com sucesso!');
        setSearchKey('');
        loadDocuments();
        loadStats();
      } else {
        toast.error(data.message || 'Erro ao importar');
      }
    } catch (error) {
      console.error('Erro:', error);
      toast.error('Erro ao importar documento');
    } finally {
      setLoading(false);
    }
  };

  // Filtrar documentos localmente
  const filteredDocuments = documents.filter(doc => {
    // Filtro de busca
    if (searchKey && searchKey.length < 44) {
      const searchLower = searchKey.toLowerCase();
      const matchKey = doc.access_key?.toLowerCase().includes(searchLower);
      const matchEmitter = doc.emitter_name?.toLowerCase().includes(searchLower);
      const matchCnpj = doc.emitter_cnpj?.includes(searchKey.replace(/\D/g, ''));
      if (!matchKey && !matchEmitter && !matchCnpj) return false;
    }
    
    // Filtro de manifestação
    if (manifestFilter === 'manifested' && !doc.manifest_status) return false;
    if (manifestFilter === 'pending' && doc.manifest_status) return false;
    
    return true;
  });

  // Handler para mapear itens NF-e para estoque
  const handleItemMapping = (idx, stockItemId) => {
    setItemMappings(prev => ({
      ...prev,
      [idx]: stockItemId
    }));
  };

  // Handler para importar itens para o estoque
  const handleImportToStock = async () => {
    if (!selectedDocument || Object.keys(itemMappings).length === 0) return;
    
    setImportingToStock(true);
    try {
      const items_mapping = Object.entries(itemMappings).map(([idx, stockItemId]) => {
        const item = nfeItems[parseInt(idx)];
        return {
          item_index: parseInt(idx),
          stock_item_id: stockItemId,
          quantity: item?.original_qty || item?.qCom || item?.quantity || 0,
          unit_cost: item?.original_unit_price || item?.vUnCom || item?.unit_value || 0,
          original_unit: item?.original_unit || item?.uCom || 'UN'
        };
      });
      
      console.log('Enviando mapeamento:', items_mapping);
      
      const response = await fetch(API_URL + '/api/sefaz/documents/' + selectedDocument.id + '/import-to-stock', {
        method: 'POST',
        headers: {
          'Authorization': 'Bearer ' + localStorage.getItem('token'),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ items_mapping })
      });
      
      if (response.ok) {
        toast.success('Itens importados para o estoque com sucesso!');
        setStockImportDialogOpen(false);
        setItemMappings({});
        loadDocuments();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao importar para o estoque');
      }
    } catch (error) {
      console.error('Erro:', error);
      toast.error('Erro ao importar para o estoque');
    } finally {
      setImportingToStock(false);
    }
  };

  // Abrir modal de importação para estoque





  return (
    <MainLayout>
      <div className="p-6 space-y-6" data-testid="fiscal-sefaz-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <Building2 className="h-7 w-7 text-orange-500" />
              Gestão Fiscal SEFAZ
            </h1>
            <p className="text-slate-500 mt-1">
              CNPJ: {formatCNPJ(COMPANY_CNPJ)} - Certificados, Documentos e Manifestação
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              onClick={handleSync} 
              disabled={syncing}
              className="bg-orange-500 hover:bg-orange-600"
              data-testid="sync-button"
            >
              {syncing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              Sincronizar SEFAZ
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-100">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600 font-medium">Total Documentos</p>
                  <p className="text-2xl font-bold text-blue-700">{stats.total_documents || 0}</p>
                </div>
                <FileText className="h-8 w-8 text-blue-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-amber-50 to-white border-amber-100">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-amber-600 font-medium">Pendentes</p>
                  <p className="text-2xl font-bold text-amber-700">{stats.pending_import || 0}</p>
                </div>
                <Clock className="h-8 w-8 text-amber-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-green-50 to-white border-green-100">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600 font-medium">Importados</p>
                  <p className="text-2xl font-bold text-green-700">{stats.imported || 0}</p>
                </div>
                <CheckCircle2 className="h-8 w-8 text-green-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-50 to-white border-purple-100">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-600 font-medium">Certificados Ativos</p>
                  <p className="text-2xl font-bold text-purple-700">{stats.active_certificates || 0}</p>
                </div>
                <Shield className="h-8 w-8 text-purple-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="documents" className="data-[state=active]:bg-white" data-testid="tab-documents">
              <FileText className="h-4 w-4 mr-2" />
              Documentos
            </TabsTrigger>
            <TabsTrigger value="certificates" className="data-[state=active]:bg-white" data-testid="tab-certificates">
              <Shield className="h-4 w-4 mr-2" />
              Certificados
            </TabsTrigger>
          </TabsList>

          {/* Tab Documentos */}
          <TabsContent value="documents" className="space-y-4">
            {/* Filtros e Busca */}
            <Card>
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        placeholder="Buscar por chave de acesso, emitente ou CNPJ..."
                        value={searchKey}
                        onChange={(e) => setSearchKey(e.target.value)}
                        className="pl-10"
                        data-testid="search-input"
                      />
                    </div>
                  </div>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-[180px]" data-testid="status-filter">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos os Status</SelectItem>
                      <SelectItem value="pending">Pendentes</SelectItem>
                      <SelectItem value="imported">Importados</SelectItem>
                      <SelectItem value="processed">Processados</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={manifestFilter} onValueChange={setManifestFilter}>
                    <SelectTrigger className="w-[180px]" data-testid="manifest-filter">
                      <SelectValue placeholder="Manifestação" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas</SelectItem>
                      <SelectItem value="pending">Não Manifestados</SelectItem>
                      <SelectItem value="manifested">Manifestados</SelectItem>
                    </SelectContent>
                  </Select>
                  {searchKey.length === 44 && (
                    <Button onClick={handleImportByKey} disabled={loading} data-testid="import-key-button">
                      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
                      Importar Chave
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Tabela de Documentos */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Documentos NF-e</CardTitle>
                <CardDescription>
                  {filteredDocuments.length} documento(s) encontrado(s)
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loadingDocs ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
                  </div>
                ) : filteredDocuments.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Nenhum documento encontrado</p>
                    <p className="text-sm mt-1">Sincronize com a SEFAZ ou importe por chave de acesso</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>NSU</TableHead>
                          <TableHead>Emitente</TableHead>
                          <TableHead>CNPJ Emitente</TableHead>
                          <TableHead>Valor</TableHead>
                          <TableHead>Data Emissão</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Manifestação</TableHead>
                          <TableHead className="text-right">Ações</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredDocuments.map((doc) => (
                          <TableRow key={doc.id} className="hover:bg-slate-50" data-testid={"doc-row-" + doc.id}>
                            <TableCell className="font-mono text-sm">{doc.nsu}</TableCell>
                            <TableCell className="max-w-[200px] truncate" title={doc.emitter_name}>
                              {doc.emitter_name || '-'}
                            </TableCell>
                            <TableCell className="font-mono text-sm">
                              {formatCNPJ(doc.emitter_cnpj)}
                            </TableCell>
                            <TableCell className="font-medium">
                              {formatCurrency(doc.total_value)}
                            </TableCell>
                            <TableCell className="text-sm">
                              {formatDate(doc.issued_at)}
                            </TableCell>
                            <TableCell>{getStatusBadge(doc.status)}</TableCell>
                            <TableCell>{getManifestBadge(doc.manifest_status)}</TableCell>
                            <TableCell className="text-right">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm" data-testid={"doc-actions-" + doc.id}>
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => handleViewDocument(doc)}>
                                    <Eye className="h-4 w-4 mr-2" />
                                    Ver Detalhes
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleOpenManifest(doc)}>
                                    <Send className="h-4 w-4 mr-2" />
                                    Manifestar
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleFetchXml(doc.id)}>
                                    <FileCode className="h-4 w-4 mr-2" />
                                    Buscar XML Completo
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => openStockImportDialog(doc)}>
                                    <Package className="h-4 w-4 mr-2" />
                                    Importar p/ Estoque
                                  </DropdownMenuItem>
                                  {!doc.manifest_status && (
                                    <DropdownMenuItem 
                                      onClick={() => handleForceManifestStatus(doc, "CIENCIA")}
                                      className="text-amber-600"
                                    >
                                      <CheckSquare className="h-4 w-4 mr-2" />
                                      Marcar como Já Manifestado
                                    </DropdownMenuItem>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab Certificados */}
          <TabsContent value="certificates" className="space-y-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-3">
                <div>
                  <CardTitle className="text-lg">Certificados Digitais A1</CardTitle>
                  <CardDescription>
                    Gerencie os certificados digitais para comunicação com a SEFAZ
                  </CardDescription>
                </div>
                <Button onClick={() => setUploadDialogOpen(true)} data-testid="upload-cert-button">
                  <Upload className="h-4 w-4 mr-2" />
                  Enviar Certificado
                </Button>
              </CardHeader>
              <CardContent>
                {loadingCerts ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
                  </div>
                ) : certificates.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Shield className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Nenhum certificado cadastrado</p>
                    <p className="text-sm mt-1">Envie um certificado A1 (.pfx/.p12) para começar</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {certificates.map((cert) => (
                      <Card key={cert.id} className={"border " + (cert.is_active ? 'border-green-200 bg-green-50/50' : '')} data-testid={"cert-card-" + cert.id}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <Shield className={"h-5 w-5 " + (cert.is_active ? 'text-green-600' : 'text-slate-400')} />
                                <span className="font-semibold">{formatCNPJ(cert.cnpj)}</span>
                                {cert.is_active && (
                                  <Badge className="bg-green-100 text-green-800 border-green-200">Ativo</Badge>
                                )}
                                {cert.is_expired && (
                                  <Badge variant="destructive">Expirado</Badge>
                                )}
                              </div>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                <div>
                                  <p className="text-slate-500">Ambiente</p>
                                  <p className="font-medium">{cert.environment}</p>
                                </div>
                                <div>
                                  <p className="text-slate-500">Tipo</p>
                                  <p className="font-medium">{cert.cert_type}</p>
                                </div>
                                <div>
                                  <p className="text-slate-500">Válido até</p>
                                  <p className="font-medium">{formatDate(cert.valid_to)}</p>
                                </div>
                                <div>
                                  <p className="text-slate-500">Dias restantes</p>
                                  <p className={"font-medium " + (cert.days_to_expire < 30 ? 'text-red-600' : 'text-green-600')}>
                                    {cert.days_to_expire !== null ? cert.days_to_expire + ' dias' : '-'}
                                  </p>
                                </div>
                              </div>
                              <div className="text-xs text-slate-400 font-mono">
                                Fingerprint: {cert.fingerprint?.substring(0, 32)}...
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleValidateCertificate(cert.id)}
                                data-testid={"validate-cert-" + cert.id}
                              >
                                <CheckCircle2 className="h-4 w-4 mr-1" />
                                Validar
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteCertificate(cert.id)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                data-testid={"delete-cert-" + cert.id}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Dialog Upload Certificado */}
        <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Enviar Certificado Digital A1</DialogTitle>
              <DialogDescription>
                Envie um certificado A1 no formato .pfx ou .p12. O certificado deve pertencer ao CNPJ {formatCNPJ(COMPANY_CNPJ)}.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="cert-file">Arquivo do Certificado (.pfx/.p12)</Label>
                <Input
                  id="cert-file"
                  type="file"
                  accept=".pfx,.p12"
                  onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                  data-testid="cert-file-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cert-password">Senha do Certificado</Label>
                <Input
                  id="cert-password"
                  type="password"
                  value={uploadPassword}
                  onChange={(e) => setUploadPassword(e.target.value)}
                  placeholder="Digite a senha do certificado"
                  data-testid="cert-password-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleUploadCertificate} disabled={uploading} data-testid="confirm-upload-button">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                Enviar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog Detalhes do Documento */}
        <Dialog open={documentDetailOpen} onOpenChange={setDocumentDetailOpen}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Detalhes do Documento</DialogTitle>
              <DialogDescription>
                NSU: {selectedDocument?.nsu} - Chave: {selectedDocument?.access_key?.substring(0, 20)}...
              </DialogDescription>
            </DialogHeader>
            {selectedDocument && (
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-500">Emitente</Label>
                    <p className="font-medium">{selectedDocument.emitter_name || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">CNPJ Emitente</Label>
                    <p className="font-mono">{formatCNPJ(selectedDocument.emitter_cnpj)}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Valor Total</Label>
                    <p className="font-medium text-lg">{formatCurrency(selectedDocument.total_value)}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Data Emissão</Label>
                    <p>{formatDate(selectedDocument.issued_at)}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Status</Label>
                    <div className="mt-1">{getStatusBadge(selectedDocument.status)}</div>
                  </div>
                  <div>
                    <Label className="text-slate-500">Manifestação</Label>
                    <div className="mt-1">{getManifestBadge(selectedDocument.manifest_status)}</div>
                  </div>
                </div>
                
                {selectedDocument.access_key && (
                  <div>
                    <Label className="text-slate-500">Chave de Acesso</Label>
                    <p className="font-mono text-xs bg-slate-100 p-2 rounded mt-1 break-all">
                      {selectedDocument.access_key}
                    </p>
                  </div>
                )}

                {/* Itens do documento */}
                {selectedDocument.items && selectedDocument.items.length > 0 && (
                  <div>
                    <Label className="text-slate-500 mb-2 block">Itens ({selectedDocument.items.length})</Label>
                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Produto</TableHead>
                            <TableHead>NCM</TableHead>
                            <TableHead className="text-right">Qtd</TableHead>
                            <TableHead className="text-right">Valor Unit.</TableHead>
                            <TableHead className="text-right">Total</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {selectedDocument.items.map((item, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="max-w-[200px] truncate">{item.product_name || item.xProd}</TableCell>
                              <TableCell className="font-mono text-xs">{item.ncm || item.NCM}</TableCell>
                              <TableCell className="text-right">{item.quantity || item.qCom} {item.unit || item.uCom}</TableCell>
                              <TableCell className="text-right">{formatCurrency(item.unit_value || item.vUnCom)}</TableCell>
                              <TableCell className="text-right font-medium">{formatCurrency(item.total_value || item.vProd)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

                {/* XML */}
                {documentXml && (
                  <div>
                    <Label className="text-slate-500 mb-2 block">XML do Documento</Label>
                    <pre className="bg-slate-900 text-green-400 p-4 rounded-lg text-xs overflow-auto max-h-[300px] whitespace-pre-wrap break-all">
                      {documentXml}
                    </pre>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setDocumentDetailOpen(false)}>
                Fechar
              </Button>
              <Button onClick={() => { setDocumentDetailOpen(false); handleOpenManifest(selectedDocument); }} data-testid="manifest-from-detail">
                <Send className="h-4 w-4 mr-2" />
                Manifestar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog Manifestação */}
        <Dialog open={manifestDialogOpen} onOpenChange={setManifestDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Manifestação do Destinatário</DialogTitle>
              <DialogDescription>
                Documento NSU: {selectedDocument?.nsu} - {selectedDocument?.emitter_name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Tipo de Manifestação</Label>
                <div className="grid grid-cols-1 gap-2">
                  {MANIFEST_TYPES.map((type) => {
                    const Icon = type.icon;
                    const isSelected = manifestType === type.value;
                  


  return (
                      <div
                        key={type.value}
                        onClick={() => setManifestType(type.value)}
                        className={"p-3 border rounded-lg cursor-pointer transition-all " + 
                          (isSelected ? 'border-orange-500 bg-orange-50 ring-2 ring-orange-200' : 'hover:border-slate-300')}
                        data-testid={"manifest-type-" + type.value}
                      >
                        <div className="flex items-center gap-3">
                          <Icon className="h-5 w-5 text-slate-600" />
                          <div>
                            <p className="font-medium">{type.label}</p>
                            <p className="text-xs text-slate-500">{type.description}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              {(manifestType === 'DESCONHECIMENTO' || manifestType === 'NAO_REALIZADA') && (
                <div className="space-y-2">
                  <Label htmlFor="justification">Justificativa (obrigatória)</Label>
                  <Textarea
                    id="justification"
                    value={manifestJustification}
                    onChange={(e) => setManifestJustification(e.target.value)}
                    placeholder="Informe o motivo da manifestação..."
                    rows={3}
                    data-testid="manifest-justification"
                  />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setManifestDialogOpen(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleManifest} 
                disabled={manifesting || !manifestType}
                className="bg-orange-500 hover:bg-orange-600"
                data-testid="confirm-manifest-button"
              >
                {manifesting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                Enviar Manifestação
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog Importação para Estoque */}
        <Dialog open={stockImportDialogOpen} onOpenChange={setStockImportDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Package className="h-5 w-5 text-green-600" />
                Importar para Estoque
              </DialogTitle>
              <DialogDescription>
                Mapeie os itens da NF-e para os itens de estoque correspondentes.
                {selectedDocument && ` NSU: ${selectedDocument.nsu} - ${selectedDocument.emitter_name}`}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {loadingNfeItems ? (
                <div className="text-center py-8 text-slate-500">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
                  <p>Carregando itens da NF-e...</p>
                </div>
              ) : nfeItems.length === 0 || !nfeItems.some(item => item.x_prod || item.xProd || item.original_qty) ? (
                <div className="text-center py-8">
                  <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-amber-500" />
                  <p className="text-lg font-medium text-slate-700">XML Completo Não Disponível</p>
                  <p className="text-sm text-slate-500 mt-2">
                    Esta NF-e possui apenas o resumo (resNFe), não o XML completo com os itens.
                  </p>
                  <p className="text-sm text-slate-500 mt-1">
                    Para importar os itens, primeiro faça a <strong>Manifestação</strong> (Ciência) 
                    e depois clique em <strong>Buscar XML Completo</strong>.
                  </p>
                </div>
              ) : (
                <>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                    <AlertTriangle className="h-4 w-4 inline mr-2" />
                    Selecione o item de estoque correspondente para cada item da NF-e. 
                    Itens não mapeados não serão importados.
                  </div>
                  
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="w-[40px]">#</TableHead>
                          <TableHead>Produto NF-e</TableHead>
                          <TableHead className="text-right">Qtd</TableHead>
                          <TableHead className="text-right">Valor Unit.</TableHead>
                          <TableHead className="w-[250px]">Item de Estoque</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {nfeItems.map((item, idx) => (
                          <TableRow key={idx} className={itemMappings[idx] ? 'bg-green-50' : ''}>
                            <TableCell className="font-mono text-sm text-slate-500">{idx + 1}</TableCell>
                            <TableCell>
                              <div className="font-medium text-sm">{item.x_prod || item.xProd || item.original_qty || item.product_name}</div>
                              <div className="text-xs text-slate-500">
                                NCM: {item.ncm || item.NCM} | EAN: {item.c_ean || item.cEAN || item.ean || 'N/A'}
                              </div>
                            </TableCell>
                            <TableCell className="text-right font-medium">
                              {item.original_qty || item.qCom || item.quantity} {item.original_unit || item.uCom || item.unit || "UN"}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatCurrency(item.original_unit_price || item.vUnCom || item.unit_value)}
                            </TableCell>
                            <TableCell>
                              <Select 
                                value={itemMappings[idx] || ''} 
                                onValueChange={(value) => handleItemMapping(idx, value)}
                              >
                                <SelectTrigger className={itemMappings[idx] ? 'border-green-500' : ''}>
                                  <SelectValue placeholder="Selecione..." />
                                </SelectTrigger>
                                <SelectContent>
                                  {stockItems.map((stockItem) => (
                                    <SelectItem key={stockItem.id} value={stockItem.id}>
                                      {stockItem.name} ({stockItem.base_unit})
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  
                  <div className="flex justify-between items-center text-sm">
                    <div className="text-slate-500">
                      {Object.keys(itemMappings).length} de {nfeItems.length} itens mapeados
                    </div>
                    <div className="text-slate-500">
                      Não encontrou o item? <a href="/stock" target="_blank" className="text-orange-600 hover:underline">Cadastre no estoque</a>
                    </div>
                  </div>
                </>
              )}
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setStockImportDialogOpen(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleImportToStock} 
                disabled={importingToStock || Object.keys(itemMappings).length !== nfeItems.length || nfeItems.length === 0}
                className="bg-green-600 hover:bg-green-700"
              >
                {importingToStock ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Package className="h-4 w-4 mr-2" />}
                Importar {Object.keys(itemMappings).length} Itens
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

      </div>
    </MainLayout>
  );
}
